import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  Briefcase,
  Clapperboard,
  Code2,
  Drama,
  Facebook,
  Handshake,
  HeartHandshake,
  Hotel,
  Instagram,
  Landmark,
  Lightbulb,
  Linkedin,
  Mail,
  Music,
  Phone,
  Plane,
  Rocket,
  ShieldCheck,
  Sparkles,
  Twitter,
  UserPlus,
  Users,
  UtensilsCrossed,
  AtSign,
} from 'lucide-react';
import {
  ButtonLink,
  Card,
  ContactForm,
  Container,
  IconChip,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
} from '../components/page';
import { Avatar, Glow } from '../components/ui';
import { fadeUp, Stagger } from '../components/motion';
import SmartLink from '../components/SmartLink';

const EMAIL = 'support@groupmoney.app';

const TIMELINE = [
  { title: 'The Idea', text: 'What if pooling money for a shared goal could be as easy as planning a dinner?', icon: Lightbulb },
  { title: 'The Innovation', text: 'We launched a platform combining smart tools, customizable rules, and a people-first approach.', icon: Rocket },
  { title: 'Today', text: "We're growing rapidly, helping thousands of groups save for trips, emergencies, events, and more.", icon: Users },
];

const VALUES = [
  {
    icon: Sparkles,
    accent: 'brand',
    title: 'Simplicity',
    text: 'We design every feature—like "Group Rules" and "Progress Tracking"—to make group finances effortless.',
    sub: 'Making complex financial tasks feel simple and intuitive for everyone.',
    span: 'lg:col-span-2',
  },
  {
    icon: ShieldCheck,
    accent: 'emerald',
    title: 'Trust & Transparency',
    text: 'With full visibility of transactions and a "Continuity Directive" for tough times, we ensure everyone feels secure.',
    sub: 'Creating a foundation of trust through complete visibility and secure systems.',
  },
  {
    icon: HeartHandshake,
    accent: 'pink',
    title: 'People First',
    text: 'Our "Resolution Center" prioritizes relationships, mediating conflicts to keep groups united even in challenging situations.',
    sub: 'Recognizing that relationships matter more than transactions and designing accordingly.',
    link: { to: '/resolution-center', label: 'Resolution Center' },
  },
  {
    icon: Lightbulb,
    accent: 'violet',
    title: 'Innovation',
    text: "From AI-powered suggestions to upcoming Group Discounts, we're always finding new ways to add value to your experience.",
    sub: 'Constantly exploring new technologies and ideas to make group finances even better.',
    span: 'lg:col-span-2',
  },
];

const EXPERTISE = [
  {
    icon: Landmark,
    title: 'Financial Experts',
    text: 'With backgrounds in fintech, banking, and financial planning, our team brings deep expertise to create a secure platform.',
  },
  {
    icon: Code2,
    title: 'Tech Innovators',
    text: 'Our engineers and designers craft intuitive, powerful tools that make managing group finances simple and accessible.',
  },
  {
    icon: Users,
    title: 'Community Builders',
    text: 'With deep insight into group dynamics, our community specialists ensure the platform strengthens relationships.',
  },
];

const TEAM = [
  { initials: 'JD', name: 'John Davis', role: 'Co-Founder', color: 'blue' },
  { initials: 'SM', name: 'Sarah Miller', role: 'Co-Founder', color: 'purple' },
  { initials: 'RJ', name: 'Robert Johnson', role: 'CTO', color: 'green' },
  { initials: 'AP', name: 'Amy Parker', role: 'Design Lead', color: 'pink' },
  { initials: 'MH', name: 'Mike Huang', role: 'Financial Director', color: 'amber' },
];

const DISCOUNT_CATEGORIES = [
  { icon: UtensilsCrossed, label: 'Restaurants' },
  { icon: Hotel, label: 'Hotels' },
  { icon: Plane, label: 'Flights' },
  { icon: Clapperboard, label: 'Movies' },
  { icon: Drama, label: 'Shows' },
  { icon: Music, label: 'Music Festivals' },
];

const SOCIALS = [
  { icon: Facebook, label: 'Facebook', href: 'https://facebook.com' },
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com' },
  { icon: Instagram, label: 'Instagram', href: 'https://instagram.com' },
  { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com' },
];

const SocialRow = ({ className }) => (
  <div className={clsx('flex gap-2', className)}>
    {SOCIALS.map(({ icon: Icon, label, href }) => (
      <SmartLink
        key={label}
        to={href}
        aria-label={label}
        className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/[0.08] text-slate-400 transition-colors duration-200 hover:border-white/20 hover:text-white"
      >
        <Icon className="h-5 w-5" />
      </SmartLink>
    ))}
  </div>
);

/* Vertical timeline card for "The Beginning" */
const Timeline = () => (
  <div className="relative">
    <Glow className="-inset-8 bg-brand-500/15" />
    <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300/50 to-transparent" />
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold tracking-tight text-white">The Beginning</h3>
        <span className="rounded-full border border-brand-400/20 bg-brand-400/10 px-3 py-1 text-sm font-semibold tabular-nums text-brand-300">
          2023
        </span>
      </div>
      <div className="relative mt-8">
        <motion.span
          aria-hidden="true"
          className="absolute bottom-2 left-[1.375rem] top-2 w-px origin-top bg-gradient-to-b from-brand-400/60 via-brand-400/30 to-transparent"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
        />
        <Stagger as="ol" className="relative space-y-8" stagger={0.15}>
          {TIMELINE.map(({ title, text, icon }) => (
            <motion.li key={title} variants={fadeUp} className="relative flex gap-5">
              {/* Opaque backing so the timeline line doesn't show through the tinted chip */}
              <span className="relative z-10 rounded-xl bg-ink-900">
                <IconChip icon={icon} />
              </span>
              <div>
                <h4 className="font-semibold text-white">{title}</h4>
                <p className="mt-1 leading-relaxed text-slate-400">{text}</p>
              </div>
            </motion.li>
          ))}
        </Stagger>
      </div>
    </div>
  </div>
);

const ContactItem = ({ icon, title, children }) => (
  <div className="flex items-start gap-4">
    <IconChip icon={icon} />
    <div>
      <h3 className="font-semibold text-white">{title}</h3>
      <div className="mt-0.5 text-slate-400">{children}</div>
    </div>
  </div>
);

const AboutPage = () => (
  <>
    <PageHero
      eyebrow="Our Story"
      highlight="Building a Better Way"
      title="to Pool Together"
      description="At Groupmoney, we believe that managing money as a group should be simple, secure, and—most importantly—bring people closer together. We're here to transform the often awkward and stressful process of pooling funds into a seamless, worry-free experience. Our mission is to empower groups—whether they're families, friends, roommates, or business partners—to achieve their financial goals with trust and transparency at the core."
    />

    {/* Our Story */}
    <Section id="story">
      <Container>
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeader align="left" title="Our Story" />
            <Reveal className="mt-6 space-y-5 text-lg leading-relaxed text-slate-400">
              <p>
                Groupmoney was born from a simple idea: what if pooling money for a shared goal could be as easy as planning a dinner? Founded by a team of innovators passionate about collaboration and technology, we saw how groups struggled with uneven contributions, lack of visibility, and unresolved conflicts.
              </p>
              <p>
                In 2023, we set out to change that, launching a platform that combines smart tools, customizable rules, and a people-first approach. Today, we're growing rapidly, helping thousands of groups save for trips, emergencies, events, and more—all while fostering stronger connections.
              </p>
            </Reveal>
            <Stagger className="mt-10 grid grid-cols-2 gap-4" stagger={0.1}>
              {[
                { value: 'Thousands', label: 'of groups using Groupmoney' },
                { value: 'Diverse', label: 'range of groups and purposes' },
              ].map((stat) => (
                <motion.div key={stat.value} variants={fadeUp} className="surface p-5 sm:p-6">
                  <p className="text-gradient-brand text-xl font-extrabold tracking-tight min-[400px]:text-2xl sm:text-4xl">{stat.value}</p>
                  <p className="mt-1 text-sm text-slate-400">{stat.label}</p>
                </motion.div>
              ))}
            </Stagger>
          </div>
          <Reveal delay={0.12}>
            <Timeline />
          </Reveal>
        </div>
      </Container>
    </Section>

    {/* Mission & Values */}
    <Section id="values">
      <Container>
        <SectionHeader
          eyebrow="What Drives Us"
          title="Our Mission & Values"
          description="We're driven by the belief that money should unite, not divide. That's why we're committed to building tools that work for every group, no matter the size or purpose."
        />
        <Stagger className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5" stagger={0.08}>
          {VALUES.map((v) => (
            <Card key={v.title} className={v.span}>
              <IconChip icon={v.icon} accent={v.accent} size="lg" />
              <h3 className="mt-6 text-2xl font-bold tracking-tight text-white">{v.title}</h3>
              <p className="mt-3 leading-relaxed text-slate-300">{v.text}</p>
              <p className="mt-4 flex-1 border-t border-white/[0.06] pt-4 text-sm leading-relaxed text-slate-500">{v.sub}</p>
              {v.link && (
                <SmartLink to={v.link.to} className="link-arrow mt-5">
                  {v.link.label}
                </SmartLink>
              )}
            </Card>
          ))}
        </Stagger>
      </Container>
    </Section>

    {/* Team */}
    <Section id="team">
      <Container>
        <SectionHeader
          eyebrow="Who We Are"
          title="Our Team"
          description="Behind Groupmoney is a dedicated team of financial experts, tech enthusiasts, and community builders bringing diverse experiences to create a platform that truly understands group dynamics."
        />
        <div className="mt-16 grid gap-5 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="surface h-full p-6 sm:p-8">
              <h3 className="text-2xl font-bold tracking-tight text-white">A Diverse Team of Experts</h3>
              <div className="mt-8 space-y-6">
                {EXPERTISE.map(({ icon, title, text }) => (
                  <div key={title} className="flex gap-4">
                    <IconChip icon={icon} />
                    <div>
                      <h4 className="font-semibold text-white">{title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-3">
            <Stagger className="grid grid-cols-2 gap-4 sm:grid-cols-3" stagger={0.06}>
              {TEAM.map((m) => (
                <motion.div key={m.name} variants={fadeUp} className="surface flex flex-col items-center p-6 text-center">
                  <Avatar initials={m.initials} color={m.color} className="h-16 w-16 text-lg" />
                  <p className="mt-4 font-semibold text-white">{m.name}</p>
                  <p className="text-sm text-slate-500">{m.role}</p>
                </motion.div>
              ))}
              <motion.div
                variants={fadeUp}
                className="surface flex flex-col items-center justify-center !border-brand-400/25 p-6 text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-brand-400/40 text-brand-300">
                  <UserPlus className="h-6 w-6" />
                </span>
                <p className="mt-4 font-semibold text-white">Team</p>
                <p className="text-sm text-brand-300">+20 Members</p>
              </motion.div>
            </Stagger>
            <Reveal className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xl leading-relaxed text-slate-400">
                Our founders lead with a passion for solving real-world problems, and our growing staff shares that commitment to making your group experience the best it can be.
              </p>
              <ButtonLink to="/contact" variant="secondary" className="shrink-0">
                Join Our Team
              </ButtonLink>
            </Reveal>
          </div>
        </div>
      </Container>
    </Section>

    {/* What's Next */}
    <Section id="whats-next">
      <Container>
        <SectionHeader
          eyebrow="Looking Forward"
          title="What's Next?"
          description="We're just getting started! Our vision is to become the go-to platform for every group needing to pool money—anywhere, anytime."
        />

        <div className="mt-16 grid gap-5 lg:grid-cols-2">
          <Reveal>
            <div className="surface h-full p-6 sm:p-8">
              <h3 className="text-2xl font-bold tracking-tight text-white">Expanding Features</h3>
              <div className="surface-inset mt-8 p-5">
                <h4 className="font-semibold text-white">Group Discounts</h4>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {DISCOUNT_CATEGORIES.map(({ icon: Icon, label }) => (
                    <SmartLink
                      key={label}
                      to="/group-discounts"
                      className="flex flex-col items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-center text-sm text-slate-300 transition-colors duration-200 hover:border-brand-400/30 hover:text-white"
                    >
                      <Icon className="h-5 w-5 text-brand-300" />
                      {label}
                    </SmartLink>
                  ))}
                </div>
              </div>
              <div className="surface-inset mt-4 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="font-semibold text-white">Partnership Portal</h4>
                  <span className="rounded-full border border-amber-400/25 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-300">
                    Coming Q3 2025
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  A powerful platform for businesses to partner with us and offer exclusive deals to Groupmoney users.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="surface h-full p-6 sm:p-8">
              <Glow className="-right-20 -top-20 h-64 w-64 bg-brand-500/15" />
              <div className="relative">
                <h3 className="text-2xl font-bold tracking-tight text-white">Join us on this journey!</h3>
                <p className="mt-3 leading-relaxed text-slate-400">
                  Whether you're a user saving with friends or a business looking to partner, we'd love to have you. Our vision is to become the go-to platform for every group needing to pool money—anywhere, anytime.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="surface-inset flex flex-col p-5">
                    <IconChip icon={Users} size="sm" />
                    <h4 className="mt-4 font-semibold text-white">For Users</h4>
                    <p className="mt-1 flex-1 text-sm leading-relaxed text-slate-400">
                      Start your group today and experience the simplicity of pooling money with total transparency and trust.
                    </p>
                    <ButtonLink to="/#get-started" className="mt-5 !px-5 !py-2.5 !text-sm">
                      Sign Up for Free
                    </ButtonLink>
                  </div>
                  <div className="surface-inset flex flex-col p-5">
                    <IconChip icon={Briefcase} accent="violet" size="sm" />
                    <h4 className="mt-4 font-semibold text-white">For Businesses</h4>
                    <p className="mt-1 flex-1 text-sm leading-relaxed text-slate-400">
                      Partner with us to offer exclusive deals to our growing community of group spenders and expand your customer base.
                    </p>
                    <ButtonLink to="/contact" variant="secondary" className="mt-5 !px-5 !py-2.5 !text-sm">
                      <Handshake className="h-4 w-4" />
                      Partner With Us
                    </ButtonLink>
                  </div>
                </div>
                <h4 className="mt-8 font-semibold text-white">Follow Our Journey</h4>
                <SocialRow className="mt-4" />
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>

    {/* Contact */}
    <Section id="contact">
      <Container>
        <div className="grid gap-14 lg:grid-cols-5 lg:gap-16">
          <div className="lg:col-span-2">
            <SectionHeader
              align="left"
              eyebrow="Get In Touch"
              title="Contact Us"
              description="Have questions or ideas? We're here to listen and grow with you. Reach out anytime!"
            />
            <Stagger className="mt-10 space-y-6" stagger={0.08}>
              <motion.div variants={fadeUp}>
                <ContactItem icon={Mail} title="Email">
                  <a href={`mailto:${EMAIL}`} className="transition-colors hover:text-white">
                    {EMAIL}
                  </a>
                </ContactItem>
              </motion.div>
              <motion.div variants={fadeUp}>
                <ContactItem icon={Phone} title="Phone">
                  <a href="tel:+15551234567" className="transition-colors hover:text-white">
                    (555) 123-4567
                  </a>
                </ContactItem>
              </motion.div>
              <motion.div variants={fadeUp}>
                <ContactItem icon={AtSign} title="Social Media">
                  @groupmoney on all platforms
                </ContactItem>
              </motion.div>
            </Stagger>
          </div>
          <Reveal delay={0.1} className="lg:col-span-3">
            <ContactForm
              title="Send us a message"
              subjectLabel="I'm interested in..."
              subjects={['Using Groupmoney', 'Business Partnership', 'Technical Support', 'Other']}
            />
          </Reveal>
        </div>
      </Container>
    </Section>
  </>
);

export default AboutPage;
